/**
 * Photo de profil — règle UNIQUE, appliquée par `/api/auth/me` (Mon profil) et par la
 * fiche collaborateur (`PUT /api/account/collaborators/:id`).
 *
 * La fenêtre « Changer la photo » proposait « Depuis l'appareil » et « Prendre une photo »,
 * qui produisent une image en data URL ; or le serveur n'acceptait que des liens https://
 * (durcissement « validation inputs », Sécurité P2+P3). Seul « Coller une URL » aboutissait :
 * les deux autres boutons échouaient à l'enregistrement, pour tout le monde (constaté sur dev
 * le 18/09/2026).
 *
 * On accepte donc, en plus des liens https:// (inchangés), une image embarquée à trois
 * conditions : format raster JPEG / PNG / WebP (jamais SVG, qui peut porter du script),
 * base64 strict, et taille bornée. L'image est réduite dans le navigateur avant envoi
 * (`compressImageFile`, côté le plus long = PROFILE_PICTURE_MAX_DIM) : quelques Ko.
 * La borne protège aussi les listes qui renvoient la photo de chaque collaborateur.
 */

/** Côté le plus long de la photo réduite dans le navigateur (affichée à ≤ 96 px). */
export const PROFILE_PICTURE_MAX_DIM = 256;
/** Longueur maximale d'une photo embarquée (≈ 150 Ko d'image). */
export const PROFILE_PICTURE_MAX_DATA_URL_LENGTH = 200_000;

const HTTPS_URL = /^https:\/\/.{3,500}$/;
const DATA_IMAGE = /^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/]+={0,2}$/;

export type ProfilePictureResult = { value: string | null; error?: undefined } | { error: string; value?: undefined };

export function parseProfilePicture(raw: unknown): ProfilePictureResult {
  if (raw === null || raw === "") return { value: null };
  if (typeof raw !== "string") return { error: "Photo de profil invalide." };
  if (HTTPS_URL.test(raw)) return { value: raw };
  if (raw.startsWith("data:")) {
    if (raw.length > PROFILE_PICTURE_MAX_DATA_URL_LENGTH) return { error: "Photo de profil trop lourde." };
    if (DATA_IMAGE.test(raw)) return { value: raw };
    return { error: "Format de photo non accepté (JPEG, PNG ou WebP)." };
  }
  return { error: "URL de photo de profil invalide (HTTPS requis)." };
}
